/**
 * Map panes and the programme extent — spec §F5.1:
 * "panes: `rasterPane z350 < choroPane z400 < adminPane z420 < vectorPane z450`".
 *
 * Every layer the app adds names one of these panes, so stacking order is a property of
 * the pane and never of insertion order. §G6's AOI-clip/swipe sets `clip-path` on
 * `rasterPane` + `choroPane` (M3), which is why they are separate panes here already.
 *
 * **`resultPane` (z460) is M4's addition and sits above all four.** A §H result is a third
 * category of map content: not a thematic layer and not the explorer's fill, but the answer
 * to a question the reader asked and waited for. It is the only thing on the map that has
 * to be visible whatever else is on — a buffer ring hidden under the settlement polygons it
 * was computed from is a result the reader would report as "the tool did nothing".
 */
import L from 'leaflet';

export const PANES = [
  /**
   * UC2's land-cover swipe (client spec `2026-10-01-UC2_LAUNCH_SPEC.md` §2) needs **two**
   * rasters on screen at once — 2017 left of the handle, 2024 right of it. `rasterPane` is
   * already clipped to the right of the handle by `Swipe.tsx`, so the left-hand raster
   * needs a pane of its own, directly under it, which the same handle clips the other way.
   * Empty unless `swipePair` is set, so nothing else in the app notices it exists.
   */
  { name: 'swipeLeftPane', z: 345 },
  { name: 'rasterPane', z: 350 },
  { name: 'choroPane', z: 400 },
  { name: 'adminPane', z: 420 },
  { name: 'vectorPane', z: 450 },
  { name: 'resultPane', z: 460 },
] as const;

export type PaneName = (typeof PANES)[number]['name'];

/** Programme extent (plan Global Constraints: lon ∈ [-13,16], lat ∈ [3.5,25]). */
export const HOME_BOUNDS = L.latLngBounds([
  [3.5, -13],
  [24.5, 16],
]);

export const MIN_ZOOM = 4;
export const MAX_ZOOM = 12;

export function createPanes(map: L.Map): void {
  for (const { name, z } of PANES) {
    const pane = map.getPane(name) ?? map.createPane(name);
    pane.style.zIndex = String(z);
  }
}

/** Padding every programmatic fit leaves around the target, in pixels. */
export const FIT_PADDING: L.PointTuple = [24, 24];

/**
 * How long a Leaflet zoom animation lasts, plus a margin — the deadline `fitTo` gives a
 * running animation before it stops waiting for the `zoomend` that should have ended it.
 * Leaflet's own transition is 250 ms (`.leaflet-zoom-anim .leaflet-zoom-animated`); a
 * background tab, a `prefers-reduced-motion` browser or a skipped `transitionend` can leave
 * one hanging, and a queued fit must not be lost when that happens.
 */
const ZOOM_ANIM_MS = 400;

/**
 * Is a **zoom** animation running right now?
 *
 * `_animatingZoom` is Leaflet-internal and has no public accessor, which is why this reads a
 * private field rather than an API: the flag is the single fact that decides whether the
 * next `fitBounds` will be honoured or silently thrown away, and Leaflet exposes no other
 * way to ask it. Guarded so a Leaflet that ever drops the field degrades to "not animating"
 * — i.e. to exactly the behaviour this module had before — rather than to a crash.
 */
function zoomAnimating(map: L.Map): boolean {
  return (map as unknown as { _animatingZoom?: boolean })._animatingZoom === true;
}

interface PendingFit {
  box: L.LatLngBounds;
  padded: boolean;
  /** The `ZOOM_ANIM_MS` deadline, so a fit that lands another way does not leave one armed. */
  timer: ReturnType<typeof setTimeout>;
  /** Detaches the `zoomend` and `unload` waiters — one pair per queued fit, never more. */
  release: () => void;
}

/** The fit each map still owes, deferred until its running zoom animation ends. */
const pendingFits = new WeakMap<L.Map, PendingFit>();

/** Forget the queued fit and everything waiting to apply it. Safe when there is none. */
function cancelPending(map: L.Map): void {
  const pending = pendingFits.get(map);
  if (!pending) return;
  pendingFits.delete(map);
  clearTimeout(pending.timer);
  pending.release();
}

/**
 * Finish a zoom animation that is never going to finish itself.
 *
 * The deadline only fires when the `zoomend` did not: a hidden tab whose CSS transition
 * never runs, a `transitionend` the browser dropped. `_animatingZoom` is then stuck **true**
 * — and that flag is not just a fact about the past, it is what `_tryAnimatedZoom` returns
 * early on, so *every* later `fitBounds` on this map would be swallowed as well. Issuing the
 * queued fit without clearing it would be the same silence the queue exists to end.
 *
 * `_onZoomTransitionEnd` is Leaflet's own handler for the event that went missing, so
 * calling it is precisely "the transition ended, late": it drops the flag, removes the
 * `leaflet-zoom-anim` class and settles the view where the animation was heading. Should a
 * future Leaflet not have it, clearing the flag alone still unblocks the fit — a map that
 * moves to where the reader asked from a slightly wrong intermediate position, rather than a
 * map that never moves again.
 */
function endStuckZoom(map: L.Map): void {
  const internals = map as unknown as {
    _animatingZoom?: boolean;
    _onZoomTransitionEnd?: () => void;
  };
  try {
    internals._onZoomTransitionEnd?.();
  } catch {
    /* an internal this Leaflet no longer has in that shape — the flag below is the fallback */
  }
  internals._animatingZoom = false;
}

/**
 * Move the view to `bounds` — the one way this app does it.
 *
 * **`map.stop()` first, always.** Leaflet's `_tryAnimatedZoom` reports "moved" and then
 * silently discards any `setView`/`fitBounds` issued while a zoom animation is still
 * running. Every one of these fits can be triggered *during* another one — a Home click
 * mid-flight (NOTES #312), a country change while the previous country's fit is still
 * animating, a search result picked while the map is easing to the last one — and the
 * symptom is not an error, it is a button that sometimes does nothing.
 *
 * That was fixed at one of the four call sites and not at the other three. Making it a
 * helper is what stops the fifth call site from having the bug again: there is now nothing
 * to remember, because `fitBounds` is not what anything calls.
 *
 * **And `map.stop()` is not enough**, which the §F5.8 cascade found: picking a country and
 * then a region without pausing between them left the map on the country, every single time.
 * `stop()` cancels a pan and a `flyTo` frame; it does not clear `_animatingZoom`, and
 * `_tryAnimatedZoom` opens with `if (this._animatingZoom) { return true; }` — it claims to
 * have handled the zoom and does nothing at all. `animate: false` does not help either: that
 * check runs *before* the options are consulted. The second fit is dropped, silently, and
 * only when it lands inside the ~250 ms of the first one's CSS transition — which is exactly
 * where a reader changing two dropdowns in a row lands, and exactly where a test with a
 * `waitForTimeout` between them does not.
 *
 * So a fit that arrives mid-zoom is **queued**, not fought: it is re-issued on `zoomend`,
 * when the map is free again and Leaflet will honour it. Nothing internal is mutated on that
 * path, a burst of changes collapses to one final fit (the last request replaces the queued
 * one), and the cost is the quarter-second the running animation had left to run.
 *
 * The other path is the `ZOOM_ANIM_MS` deadline, for the `zoomend` that never comes at all
 * — and there the fit is *applied*, not re-queued, after `endStuckZoom` has cleared the
 * animation that stopped: waiting again for the event that already failed to arrive is how a
 * deadline turns into an infinite loop of deadlines.
 *
 * An invalid `bounds` (an empty FeatureCollection's, a unit with no geometry) is a no-op
 * rather than a Leaflet exception — the caller's answer to "where is this?" was "nowhere",
 * and throwing on it would take the map down with it.
 */
export function fitTo(map: L.Map, bounds: L.LatLngBoundsExpression, padded = true): void {
  const box = bounds instanceof L.LatLngBounds ? bounds : L.latLngBounds(bounds);
  if (!box.isValid()) return;
  map.stop();

  if (!zoomAnimating(map)) {
    // Whatever was queued is superseded by the fit about to happen — including its deadline.
    cancelPending(map);
    map.fitBounds(box, padded ? { padding: FIT_PADDING } : undefined);
    return;
  }

  const queued = pendingFits.get(map);
  if (queued) {
    // One waiter per map, reading whichever request is latest: a burst collapses to one fit.
    queued.box = box;
    queued.padded = padded;
    return;
  }

  const run = () => {
    const next = pendingFits.get(map);
    if (!next) return;
    cancelPending(map);
    // **Not** `fitTo` again. This is also what the deadline calls, and the deadline is for
    // the case where `_animatingZoom` is *still* up — re-entering would queue the fit a
    // second time and wait for another `zoomend` that is not coming either, for ever. So the
    // wedged animation is ended first (`endStuckZoom`) and the fit is issued outright.
    if (zoomAnimating(map)) endStuckZoom(map);
    map.stop();
    map.fitBounds(next.box, next.padded ? { padding: FIT_PADDING } : undefined);
  };
  // A map torn down while a fit is queued: `unload` is Leaflet's own "this map is gone", and
  // a quarter-second later `fitBounds` on it would be an exception thrown at nobody.
  const drop = () => cancelPending(map);

  pendingFits.set(map, {
    box,
    padded,
    timer: setTimeout(run, ZOOM_ANIM_MS),
    release: () => {
      map.off('zoomend', run);
      map.off('unload', drop);
    },
  });
  map.once('zoomend', run);
  map.once('unload', drop);
}

/**
 * Class the §F5.10 crosshair tools put on the map container; `base.css` hangs the
 * pointer-events override off it.
 */
export const TOOL_ARMED_CLASS = 'tool-armed';

/**
 * Arm (or disarm) the map for a §F5.10 crosshair tool.
 *
 * While measure or draw-AOI is running a click is a vertex and nothing else: without this,
 * dropping a vertex on a region also *selects* it, and the §F5.7 profile panel slides out
 * over the map the reader is drawing on.
 *
 * It has to be a class rather than `pane.style.pointerEvents = 'none'`, because Leaflet's
 * own stylesheet gives every interactive path `pointer-events: auto` — a descendant
 * declaration, which beats anything set on the ancestor pane. The rule in `base.css`
 * reaches the paths themselves. The map container still receives the click, which is the
 * event the tools listen for.
 */
export function setToolArmed(map: L.Map, armed: boolean): void {
  map.getContainer().classList.toggle(TOOL_ARMED_CLASS, armed);
}
